import React from 'react';
import Box from '@components/Atoms/Box';
import Chip from '@components/Atoms/Chip';
import {
  useContentFilterContext,
  useContentFilterLessonContext,
  useContentFilterSkillContext,
  useContentFilterStandardContext,
} from '@components/ContentFilters/ContentFilterProvider';
import { cleanText } from '@components/QuestionGenerationService/utils/helpers';
import SubjectIcon from '@mui/icons-material/Subject';
import { BLOOMS_MAP } from '@components/Selectors/BloomsSelector/BloomsSelector';
import { DOK_MAP } from '@components/Selectors/DokSelector/DokSelector';
import ShowIf from '@components/Atoms/ShowIf';
import { questionTypeToName } from '@components/ContentFilters/utils';
import { useInteractionContext } from '@components/ElementAnalytics/InteractionContext';

const FilterChips: React.FC = () => {
  const { getTestId } = useInteractionContext();
  const {
    selectedBlooms,
    selectedDok,
    selectedPassage,
    selectedReadability,
    selectedGrade,
    selectedQuestionType,
    setSelectedDok,
    setSelectedBlooms,
    setSelectedPassage,
    setSelectedGrade,
    setSelectedReadability,
    setSelectedQuestionType,
  } = useContentFilterContext();

  const { getSelectedLessons, setSelectedLessons } = useContentFilterLessonContext();
  const { getSelectedStandardObjects, setSelectedStandards } =
    useContentFilterStandardContext();
  const { setSelectedSkills, getSelectedSkillObjects } = useContentFilterSkillContext();
  const selectedLessons = getSelectedLessons();
  const selectedStandards = getSelectedStandardObjects();
  const selectedSkills = getSelectedSkillObjects();

  const bloomLabel =
    selectedBlooms.length === 1
      ? `Blooms: ${BLOOMS_MAP[selectedBlooms[0]]}`
      : `Blooms: (${selectedBlooms.length}) selected`;

  const dokLabel =
    selectedDok.length === 1
      ? `DOK: ${DOK_MAP[selectedDok[0]]}`
      : `DOK: (${selectedDok.length}) selected`;

  const questionTypeLabel =
    selectedQuestionType.length === 1
      ? `Question Type: ${questionTypeToName(selectedQuestionType[0])}`
      : `Question Type: (${selectedQuestionType.length}) selected`;

  const readabilityLabel =
    selectedReadability.length === 1
      ? `Readability: ${selectedReadability[0]}`
      : `Readability: (${selectedReadability.length}) selected`;

  const gradeLabel =
    selectedGrade.length === 1
      ? `Grade: ${selectedGrade[0]}`
      : `Grade: (${selectedGrade.length}) selected`;

  return (
    <Box data-testid={getTestId('filter-chips-wrapper')}>
      {selectedLessons?.length > 0 && (
        <Chip
          key="lesson-chip"
          label={`${selectedLessons.length} Lesson(s)`}
          clickable={false}
          title={selectedLessons.map((l) => `${l.name}`).join('\n')}
          color="default"
          size="small"
          variant={undefined}
          onDelete={() => {
            setSelectedLessons([]);
          }}
        />
      )}
      {selectedStandards?.length > 0 && (
        <Chip
          key="standard-chip"
          label={`${selectedStandards.length} Standard(s)`}
          clickable={false}
          title={selectedStandards.map((s) => `${s.code}: ${s.description}`).join('\n')}
          color="default"
          size="small"
          variant={undefined}
          onDelete={() => {
            setSelectedStandards([]);
          }}
        />
      )}
      {selectedSkills?.length > 0 && (
        <Chip
          key="skill-chip"
          label={`${selectedSkills.length} Skill(s)`}
          clickable={false}
          color="default"
          size="small"
          title={selectedSkills.map((s) => s.name).join('\n')}
          variant={undefined}
          onDelete={() => {
            setSelectedSkills([]);
          }}
        />
      )}
      {selectedBlooms.length > 0 && (
        <Chip
          key="bloom-chip"
          label={bloomLabel}
          clickable={false}
          color="default"
          size="small"
          variant={undefined}
          onDelete={() => setSelectedBlooms([])}
        />
      )}
      {selectedDok.length > 0 && (
        <Chip
          key="dok-chip"
          label={dokLabel}
          clickable={false}
          color="default"
          size="small"
          variant={undefined}
          onDelete={() => setSelectedDok([])}
        />
      )}
      {selectedQuestionType.length > 0 && (
        <Chip
          key="question-type-chip"
          label={questionTypeLabel}
          clickable={false}
          color="default"
          size="small"
          variant={undefined}
          onDelete={() => setSelectedQuestionType([])}
          sx={{ textTransform: 'capitalize' }}
        />
      )}
      {selectedPassage && (
        <Chip
          key="passage-chip"
          label={
            selectedPassage?.data?.heading
              ? cleanText(selectedPassage?.data?.heading)
              : cleanText(selectedPassage?.data?.content || '')
          }
          icon={<SubjectIcon />}
          color="default"
          size="small"
          clickable={false}
          sx={{
            maxWidth: '20rem',
          }}
          onDelete={() => {
            setSelectedPassage(null);
          }}
        />
      )}
      {selectedReadability.length > 0 && (
        <Chip
          key="readability-chip"
          label={readabilityLabel}
          clickable={false}
          color="default"
          size="small"
          variant={undefined}
          onDelete={() => setSelectedReadability([])}
        />
      )}
      {selectedGrade.length > 0 && (
        <Chip
          key="grade-chip"
          label={gradeLabel}
          clickable={false}
          color="default"
          size="small"
          variant={undefined}
          onDelete={() => setSelectedGrade([])}
        />
      )}
      <ShowIf
        If={
          !!selectedStandards.length ||
          !!selectedSkills.length ||
          !!selectedBlooms.length ||
          !!selectedDok.length ||
          !!selectedPassage ||
          !!selectedReadability.length ||
          !!selectedGrade.length ||
          !!selectedQuestionType.length
        }
      >
        <Chip
          key="clear-chip"
          label="Clear All"
          clickable
          color="default"
          size="small"
          variant="outlined"
          onClick={() => {
            setSelectedStandards([]);
            setSelectedSkills([]);
            setSelectedBlooms([]);
            setSelectedDok([]);
            setSelectedQuestionType([]);
            setSelectedPassage(null);
            setSelectedReadability([]);
            setSelectedGrade([]);
            setSelectedLessons([]);
          }}
        />
      </ShowIf>
    </Box>
  );
};

export default FilterChips;
